import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset } from "../../services/authApiService";
import logo from "../../images/logo-full.png";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Extraer uid y token de los query params (del email del dashboard)
  const uidFromUrl = searchParams.get("uid") || "";
  const tokenFromUrl = searchParams.get("token") || "";

  const [uid, setUid] = useState(uidFromUrl);
  const [token, setToken] = useState(tokenFromUrl);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    hasMinLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const validatePassword = (pwd) => {
    setNewPassword(pwd);
    setPasswordValidation({
      hasMinLength: pwd.length >= 8,
      hasLowercase: /[a-z]/.test(pwd),
      hasUppercase: /[A-Z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
    });
  };

  const isPasswordValid =
    passwordValidation.hasMinLength &&
    passwordValidation.hasLowercase &&
    passwordValidation.hasUppercase &&
    passwordValidation.hasNumber &&
    passwordValidation.hasSpecialChar;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!uid) {
      setError("UID es requerido. Por favor usa el enlace completo del email.");
      return;
    }

    if (!token) {
      setError("Token es requerido. Por favor usa el enlace completo del email.");
      return;
    }

    if (!isPasswordValid) {
      setError("La contraseña debe cumplir todos los requisitos");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);

    try {
      console.log("📡 Confirmando reset de contraseña:", { uid, token, hasEmail: !!email });
      
      const result = await confirmPasswordReset(uid, token, newPassword, email || undefined);
      
      console.log("✅ Reset confirmado exitosamente:", result);
      
      setSuccess(true);
      
      // Redirigir al login después de 3 segundos
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      console.error("❌ Error al confirmar reset:", err);
      setError(err.message || "Error al resetear contraseña. Verifica que el token no haya expirado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="authincation h-100 p-meddle">
      <div className="container h-100">
        <div className="row justify-content-center h-100 align-items-center">
          <div className="col-md-6">
            <div className="authincation-content">
              <div className="row no-gutters">
                <div className="col-xl-12">
                  <div className="auth-form">
                    <div className="text-center mb-3">
                      <Link to="/login">
                        <img src={logo} alt="Fund8 Logo" />
                      </Link>
                    </div>
                    <h4 className="text-center mb-4">Restablecer Contraseña</h4>

                    {success && (
                      <div className="alert alert-success">
                        <strong>✓ Contraseña Restablecida Exitosamente</strong>
                        <p className="mb-0 mt-2">
                          Tu contraseña ha sido actualizada. Serás redirigido al login en unos segundos...
                        </p>
                      </div>
                    )}

                    {!success && (
                      <>
                        {!uidFromUrl || !tokenFromUrl ? (
                          <div className="alert alert-warning">
                            <strong>⚠️ Enlace Inválido</strong>
                            <p className="mb-0 mt-2">
                              Por favor usa el enlace completo que recibiste en tu email. 
                              Si no tienes el enlace, solicita un nuevo reset desde la página de login.
                            </p>
                          </div>
                        ) : (
                          <div className="alert alert-info">
                            <strong>✓ Enlace Válido</strong>
                            <p className="mb-0 mt-2">
                              Ingresa tu nueva contraseña abajo. Asegúrate de cumplir todos los requisitos.
                            </p>
                          </div>
                        )}

                        {error && (
                          <div className="alert alert-danger">{error}</div>
                        )}
                      </>
                    )}

                    {!success && (
                      <form onSubmit={onSubmit}>
                        {(!uidFromUrl || !tokenFromUrl) && (
                          <>
                            <div className="form-group mb-3">
                              <label>
                                <strong>UID (del email)</strong>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Ej: 12"
                                value={uid}
                                onChange={(e) => setUid(e.target.value)}
                                disabled={loading}
                              />
                            </div>
                            <div className="form-group mb-3">
                              <label>
                                <strong>Token (del email)</strong>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Pega el token completo aquí"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                disabled={loading}
                              />
                            </div>
                          </>
                        )}

                        <div className="form-group mb-3">
                          <label>
                            <strong>Email (Opcional pero recomendado)</strong>
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            placeholder="tu@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                          />
                          <small className="text-muted">
                            Ayuda a sincronizar la contraseña correctamente
                          </small>
                        </div>

                      <div className="form-group mb-3">
                        <label>
                          <strong>New Password</strong>
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => validatePassword(e.target.value)}
                          disabled={loading}
                        />
                        <div className="password-requirements mt-2">
                          <div
                            className={
                              passwordValidation.hasMinLength
                                ? "text-success"
                                : "text-muted"
                            }
                          >
                            {passwordValidation.hasMinLength ? "✓" : "○"}{" "}
                            Mínimo 8 caracteres
                          </div>
                          <div
                            className={
                              passwordValidation.hasLowercase
                                ? "text-success"
                                : "text-muted"
                            }
                          >
                            {passwordValidation.hasLowercase ? "✓" : "○"}{" "}
                            Al menos una letra minúscula
                          </div>
                          <div
                            className={
                              passwordValidation.hasUppercase
                                ? "text-success"
                                : "text-muted"
                            }
                          >
                            {passwordValidation.hasUppercase ? "✓" : "○"}{" "}
                            Al menos una letra mayúscula
                          </div>
                          <div
                            className={
                              passwordValidation.hasNumber
                                ? "text-success"
                                : "text-muted"
                            }
                          >
                            {passwordValidation.hasNumber ? "✓" : "○"}{" "}
                            Al menos un número
                          </div>
                          <div
                            className={
                              passwordValidation.hasSpecialChar
                                ? "text-success"
                                : "text-muted"
                            }
                          >
                            {passwordValidation.hasSpecialChar ? "✓" : "○"}{" "}
                            Al menos un carácter especial (!@#$%^&*)
                          </div>
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label>
                          <strong>Confirm Password</strong>
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          placeholder="Confirm new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={loading}
                        />
                      </div>

                        <div className="text-center mt-4">
                          <button
                            type="submit"
                            className="btn btn-primary btn-block"
                            disabled={loading || !isPasswordValid || !uid || !token}
                          >
                            {loading ? "Restableciendo..." : "RESTABLECER CONTRASEÑA"}
                          </button>
                        </div>
                      </form>
                    )}

                    <div className="new-account mt-3 text-center">
                      <p>
                        ¿Recordaste tu contraseña?{" "}
                        <Link to="/login" className="text-primary">
                          Iniciar Sesión
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

